'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import type {
  DreamTwin,
  DreamTwinContextValue,
  DreamTwinRecommendation,
} from './types';

// ─── Storage Key ──────────────────────────────────────────────────────────────

const STORAGE_KEY = 'dreamTwin_v1';

// ─── Default DreamTwin ────────────────────────────────────────────────────────

function createDefaultTwin(projectId?: string): DreamTwin {
  const now = new Date().toISOString();
  return {
    projectId,
    identity: {},
    brand: {},
    audience: {},
    products: {},
    marketing: {},
    website: {},
    plan: {},
    operations: {
      hasPrivacyPolicy: true,
      hasRefundPolicy: true,
    },
    analytics: {},
    learning: {
      acceptedSuggestions: [],
      rejectedSuggestions: [],
      userEditHistory: [],
      interactionHistory: [],
    },
    recommendations: [],
    conversationHistory: [],
    createdAt: now,
    updatedAt: now,
  };
}

// ─── Dot-path setter ──────────────────────────────────────────────────────────

function setNestedValue(obj: any, path: string, value: any): any {
  const keys = path.split('.');
  if (keys.length === 1) return { ...obj, [keys[0]]: value };
  const head = keys[0];
  const rest = keys.slice(1).join('.');
  return { ...obj, [head]: setNestedValue(obj[head] ?? {}, rest, value) };
}

// ─── Context ──────────────────────────────────────────────────────────────────

const DreamTwinContext = createContext<DreamTwinContextValue | null>(null);

export function DreamTwinProvider({ children }: { children: React.ReactNode }) {
  const [twin, setTwin] = useState<DreamTwin | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // ── Persist to sessionStorage ─────────────────────────────────────────────
  useEffect(() => {
    if (twin) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(twin));
      } catch {
        // quota exceeded — ignore
      }
    }
  }, [twin]);

  // ── Restore from sessionStorage on mount ─────────────────────────────────
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        setTwin(JSON.parse(stored));
        setIsInitialized(true);
      }
    } catch {
      // corrupted — ignore
    }
  }, []);

  // ── Init ──────────────────────────────────────────────────────────────────
  const initTwin = useCallback((projectId?: string) => {
    setTwin(prev => {
      if (prev && (!projectId || prev.projectId === projectId)) return prev;
      return createDefaultTwin(projectId);
    });
    setIsInitialized(true);
  }, []);

  // ── Update top-level section ──────────────────────────────────────────────
  const updateTwin = useCallback(<K extends keyof DreamTwin>(
    section: K,
    value: DreamTwin[K]
  ) => {
    setTwin(prev => {
      if (!prev) return prev;
      return { ...prev, [section]: value, updatedAt: new Date().toISOString() };
    });
  }, []);

  // ── Update nested field by dot-path ──────────────────────────────────────
  const updateTwinField = useCallback((path: string, value: any) => {
    setTwin(prev => {
      if (!prev) return prev;
      const updated = setNestedValue(prev, path, value) as DreamTwin;
      updated.updatedAt = new Date().toISOString();
      return updated;
    });
  }, []);

  // ── Sync from DreamMemory ─────────────────────────────────────────────────
  const syncFromMemory = useCallback((memory: any) => {
    if (!memory) return;
    setIsSyncing(true);
    setTwin(prev => {
      const base = prev ?? createDefaultTwin(memory.projectId);
      const now = new Date().toISOString();

      // Identity
      const identity = { ...base.identity };
      if (memory.identity?.businessName) identity.businessName = memory.identity.businessName;
      if (memory.identity?.businessType) identity.businessType = memory.identity.businessType;
      if (memory.identity?.industry) identity.industry = memory.identity.industry;
      if (memory.identity?.niche) identity.niche = memory.identity.niche;

      // Brand
      const brand = { ...base.brand };
      if (memory.brand?.brandPersonality?.length) brand.brandPersonality = memory.brand.brandPersonality;
      if (memory.brand?.brandArchetype) brand.brandArchetype = memory.brand.brandArchetype;
      if (memory.brand?.tone) brand.tone = memory.brand.tone;
      if (memory.brand?.colors) brand.colors = memory.brand.colors;
      if (memory.brand?.typography) brand.typography = memory.brand.typography;
      if (memory.brand?.preferredVisualStyle) brand.visualStyle = memory.brand.preferredVisualStyle;
      if (memory.brand?.imageStyle) brand.imageStyle = memory.brand.imageStyle;

      // Audience
      const audience = { ...base.audience };
      if (memory.audience?.targetAudience) audience.primaryAudience = memory.audience.targetAudience;
      if (memory.audience?.secondaryAudience) audience.secondaryAudience = memory.audience.secondaryAudience;
      if (memory.audience?.customerPersona) audience.customerPersona = memory.audience.customerPersona;
      if (memory.audience?.painPoints?.length) audience.painPoints = memory.audience.painPoints;
      if (memory.audience?.desires?.length) audience.desires = memory.audience.desires;

      // Products
      const products = { ...base.products };
      if (memory.products?.products?.length) products.products = memory.products.products;
      if (memory.products?.services?.length) products.services = memory.products.services;
      if (memory.products?.pricingStrategy) products.pricingStrategy = memory.products.pricingStrategy;
      if (memory.products?.pricingLevel) products.pricingLevel = memory.products.pricingLevel;
      if (memory.products?.monetizationMethods?.length) products.monetizationMethods = memory.products.monetizationMethods;

      // Marketing
      const marketing = { ...base.marketing };
      if (memory.strategy?.marketingStrategy) marketing.marketingStrategy = memory.strategy.marketingStrategy;
      if (memory.strategy?.marketingChannels?.length) marketing.marketingChannels = memory.strategy.marketingChannels;
      if (memory.strategy?.contentStrategy) marketing.contentStrategy = memory.strategy.contentStrategy;

      // Plan
      const plan = { ...base.plan };
      if (memory.strategy?.launchPlan) plan.launchPlan = memory.strategy.launchPlan;
      if (memory.project?.projectGoals?.length) plan.shortTermGoals = memory.project.projectGoals;
      if (memory.project?.projectStatus) {
        plan.launchStatus = memory.project.projectStatus === 'launched' ? 'launched'
          : memory.project.projectStatus === 'in-progress'? 'in-progress' :'not-started';
      }

      // Analytics
      const analytics = { ...base.analytics };
      if (memory.project?.dreamScore) analytics.dreamHealthScore = memory.project.dreamScore;
      if (memory.generatedVisuals?.length) analytics.generationsCount = memory.generatedVisuals.length;

      return {
        ...base,
        identity,
        brand,
        audience,
        products,
        marketing,
        plan,
        analytics,
        updatedAt: now,
        lastSyncedAt: now,
      };
    });
    setIsInitialized(true);
    setIsSyncing(false);
  }, []);

  // ── Sync from BusinessProfile ─────────────────────────────────────────────
  const syncFromProfile = useCallback((profile: any) => {
    if (!profile) return;
    setTwin(prev => {
      const base = prev ?? createDefaultTwin();
      const now = new Date().toISOString();

      const identity = { ...base.identity };
      if (profile.business_type) identity.businessType = profile.business_type;
      if (profile.industry) identity.industry = profile.industry;
      if (profile.niche) identity.niche = profile.niche;

      const brand = { ...base.brand };
      if (profile.brand_personality?.length) brand.brandPersonality = profile.brand_personality;
      if (profile.brand_archetype) brand.brandArchetype = profile.brand_archetype;
      if (profile.tone) brand.tone = profile.tone;
      if (profile.color_direction) {
        brand.colors = {
          primary: profile.color_direction.primary,
          secondary: profile.color_direction.secondary,
          accent: profile.color_direction.accent,
          mood: profile.color_direction.mood,
        };
      }
      if (profile.typography_direction) {
        brand.typography = {
          headline: profile.typography_direction.headline,
          body: profile.typography_direction.body,
          accent: profile.typography_direction.accent,
        };
      }
      if (profile.visual_style) brand.visualStyle = profile.visual_style;
      if (profile.image_style) brand.imageStyle = profile.image_style;

      const audience = { ...base.audience };
      if (profile.audience?.primary) audience.primaryAudience = profile.audience.primary;
      if (profile.audience?.secondary) audience.secondaryAudience = profile.audience.secondary;
      if (profile.audience?.pain_points?.length) audience.painPoints = profile.audience.pain_points;
      if (profile.audience?.desires?.length) audience.desires = profile.audience.desires;

      const products = { ...base.products };
      if (profile.pricing_level) products.pricingLevel = profile.pricing_level;
      if (profile.monetization_methods?.length) products.monetizationMethods = profile.monetization_methods;
      if (profile.recommended_products_services?.length) {
        products.products = profile.recommended_products_services;
      }

      const marketing = { ...base.marketing };
      if (profile.marketing_channels?.length) marketing.marketingChannels = profile.marketing_channels;
      if (profile.keywords?.length) marketing.keywords = profile.keywords;
      if (profile.content_strategy?.pillars?.length) marketing.contentPillars = profile.content_strategy.pillars;

      const plan = { ...base.plan };
      if (profile.business_goals?.short_term?.length) plan.shortTermGoals = profile.business_goals.short_term;
      if (profile.business_goals?.long_term?.length) plan.longTermGoals = profile.business_goals.long_term;

      return {
        ...base,
        identity,
        brand,
        audience,
        products,
        marketing,
        plan,
        updatedAt: now,
        lastSyncedAt: now,
      };
    });
    setIsInitialized(true);
  }, []);

  // ── Add recommendation ────────────────────────────────────────────────────
  const addRecommendation = useCallback((rec: Omit<DreamTwinRecommendation, 'id' | 'createdAt'>) => {
    setTwin(prev => {
      if (!prev) return prev;
      const id = `rec_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const newRec: DreamTwinRecommendation = {
        ...rec,
        id,
        createdAt: new Date().toISOString(),
      };
      // Avoid duplicates by title
      const exists = prev.recommendations.some(r => r.title === rec.title && !r.dismissed);
      if (exists) return prev;
      return {
        ...prev,
        recommendations: [newRec, ...prev.recommendations].slice(0, 20),
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  // ── Dismiss recommendation ────────────────────────────────────────────────
  const dismissRecommendation = useCallback((id: string) => {
    setTwin(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        recommendations: prev.recommendations.map(r =>
          r.id === id ? { ...r, dismissed: true } : r
        ),
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  // ── Record interaction ────────────────────────────────────────────────────
  const recordInteraction = useCallback((
    type: 'question' | 'recommendation' | 'task',
    content: string,
    outcome?: 'accepted' | 'rejected'
  ) => {
    setTwin(prev => {
      if (!prev) return prev;
      const interaction = {
        type,
        content,
        timestamp: new Date().toISOString(),
        outcome,
      };
      const history = [...(prev.learning.interactionHistory ?? []), interaction].slice(-50);
      return {
        ...prev,
        learning: { ...prev.learning, interactionHistory: history },
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  // ── Build AI context string ───────────────────────────────────────────────
  const buildAIContext = useCallback((): string => {
    if (!twin) return '';
    const { identity, brand, audience, products, marketing, website, plan, operations, analytics } = twin;

    const lines: string[] = ['=== BUSINESS CONTEXT ==='];

    if (identity.businessName) lines.push(`Business: ${identity.businessName}`);
    if (identity.businessType) lines.push(`Type: ${identity.businessType}`);
    if (identity.industry) lines.push(`Industry: ${identity.industry}`);
    if (identity.niche) lines.push(`Niche: ${identity.niche}`);
    if (identity.mission) lines.push(`Mission: ${identity.mission}`);
    if (identity.vision) lines.push(`Vision: ${identity.vision}`);
    if (identity.stage) lines.push(`Stage: ${identity.stage}`);

    if (brand.tone) lines.push(`Brand Tone: ${brand.tone}`);
    if (brand.brandPersonality?.length) lines.push(`Brand Personality: ${brand.brandPersonality.join(', ')}`);
    if (brand.brandVoice) lines.push(`Brand Voice: ${brand.brandVoice}`);
    if (brand.colors?.primary) lines.push(`Brand Colors: Primary=${brand.colors.primary}, Secondary=${brand.colors.secondary ?? 'N/A'}`);

    if (audience.primaryAudience) lines.push(`Target Audience: ${audience.primaryAudience}`);
    if (audience.painPoints?.length) lines.push(`Customer Pain Points: ${audience.painPoints.slice(0, 3).join(', ')}`);
    if (audience.desires?.length) lines.push(`Customer Desires: ${audience.desires.slice(0, 3).join(', ')}`);

    if (products.products?.length) {
      lines.push(`Products: ${products.products.slice(0, 3).map(p => p.name).join(', ')}`);
    }
    if (products.services?.length) {
      lines.push(`Services: ${products.services.slice(0, 3).map(s => s.name).join(', ')}`);
    }
    if (products.pricingLevel) lines.push(`Pricing Level: ${products.pricingLevel}`);
    if (products.pricingStrategy) lines.push(`Pricing Strategy: ${products.pricingStrategy}`);

    if (marketing.marketingChannels?.length) lines.push(`Marketing Channels: ${marketing.marketingChannels.join(', ')}`);
    if (marketing.marketingStrategy) lines.push(`Marketing Strategy: ${marketing.marketingStrategy}`);
    if (marketing.contentPillars?.length) lines.push(`Content Pillars: ${marketing.contentPillars.join(', ')}`);

    if (website.hasWebsite) lines.push(`Website: ${website.websiteUrl ?? 'In progress'}`);
    if (plan.launchStatus) lines.push(`Launch Status: ${plan.launchStatus}`);
    if (plan.shortTermGoals?.length) lines.push(`Short-term Goals: ${plan.shortTermGoals.slice(0, 3).join(', ')}`);

    if (analytics.dreamHealthScore) lines.push(`DreamHealth Score: ${analytics.dreamHealthScore}%`);
    if (analytics.launchReadinessScore) lines.push(`Launch Readiness: ${analytics.launchReadinessScore}%`);

    const missing = getMissingItemsInternal(twin);
    if (missing.length) lines.push(`\nKnown Gaps: ${missing.slice(0, 5).join(', ')}`);

    return lines.join('\n');
  }, [twin]);

  // ── Get missing items ─────────────────────────────────────────────────────
  const getMissingItems = useCallback((): string[] => {
    if (!twin) return [];
    return getMissingItemsInternal(twin);
  }, [twin]);

  // ── Get next best action ──────────────────────────────────────────────────
  const getNextBestAction = useCallback((): DreamTwinRecommendation | null => {
    if (!twin) return null;
    const active = twin.recommendations.filter(r => !r.dismissed);
    if (!active.length) return null;
    // Sort by impact then estimatedGain
    const sorted = [...active].sort((a, b) => {
      const impactOrder = { high: 3, medium: 2, low: 1 };
      const impactDiff = (impactOrder[b.impact] ?? 0) - (impactOrder[a.impact] ?? 0);
      if (impactDiff !== 0) return impactDiff;
      return (b.estimatedGain ?? 0) - (a.estimatedGain ?? 0);
    });
    return sorted[0];
  }, [twin]);

  // ── Conversation history ──────────────────────────────────────────────────
  const addConversationMessage = useCallback((role: 'user' | 'assistant', content: string) => {
    setTwin(prev => {
      if (!prev) return prev;
      const history = [...prev.conversationHistory, { role, content }].slice(-40);
      return { ...prev, conversationHistory: history, updatedAt: new Date().toISOString() };
    });
  }, []);

  const clearConversation = useCallback(() => {
    setTwin(prev => {
      if (!prev) return prev;
      return { ...prev, conversationHistory: [], updatedAt: new Date().toISOString() };
    });
  }, []);

  const value: DreamTwinContextValue = {
    twin,
    isInitialized,
    isSyncing,
    initTwin,
    updateTwin,
    updateTwinField,
    syncFromMemory,
    syncFromProfile,
    addRecommendation,
    dismissRecommendation,
    recordInteraction,
    buildAIContext,
    getMissingItems,
    getNextBestAction,
    addConversationMessage,
    clearConversation,
  };

  return (
    <DreamTwinContext.Provider value={value}>
      {children}
    </DreamTwinContext.Provider>
  );
}

export function useDreamTwin(): DreamTwinContextValue {
  const ctx = useContext(DreamTwinContext);
  if (!ctx) throw new Error('useDreamTwin must be used within DreamTwinProvider');
  return ctx;
}

// ─── Internal helper (used by both getMissingItems and buildAIContext) ────────

function getMissingItemsInternal(twin: DreamTwin): string[] {
  const missing: string[] = [];
  const { identity, brand, audience, products, marketing, website, operations } = twin;

  if (!identity.businessName) missing.push('Business name');
  if (!identity.mission) missing.push('Mission statement');
  if (!identity.vision) missing.push('Vision statement');
  if (!brand.tone) missing.push('Brand tone');
  if (!brand.colors?.primary) missing.push('Brand colors');
  if (!audience.primaryAudience) missing.push('Target audience');
  if (!products.products?.length && !products.services?.length) missing.push('Products or services');
  if (!products.pricingStrategy) missing.push('Pricing strategy');
  if (!marketing.marketingChannels?.length) missing.push('Marketing channels');
  if (!marketing.emailMarketing) missing.push('Email marketing');
  if (!marketing.leadMagnet) missing.push('Lead magnet');
  if (!website.hasWebsite) missing.push('Website');
  if (!website.hasDomain) missing.push('Custom domain');
  if (!operations.hasStripe) missing.push('Payment processor');
  if (!operations.hasEmailAutomation) missing.push('Email automation');
  if (!operations.hasAnalytics) missing.push('Analytics');

  return missing;
}
